import { useEffect, useLayoutEffect, useRef } from "react";

// useInfiniteScrollTop — infinite scroll UPWARDS for a backwards-paginated
// message stream. Latest messages load first (fn_conversation_read with no
// p_before); older pages prepend above via p_before.
//
// HOW IT TRIGGERS: a passive `scroll` listener on the scroll container. When
// scrollTop is within `threshold` px of the top, and we have more pages and
// aren't already loading, we snapshot scrollHeight and call onLoadMore(). A
// scroll listener is used (not IntersectionObserver) because IO fires once on
// observe — at mount scrollTop is 0 (the scroll-to-bottom rAF hasn't run yet),
// so IO would report the top sentinel as "intersecting" and trigger a premature
// load before the user has done anything.
//
// ARM GUARD: the listener is "armed" 300ms after `ready` becomes true, giving
// the scroll-to-bottom rAFs time to pin to the bottom. Before that, scrollTop
// is 0 and we must NOT fire.
//
// SCROLL ANCHORING: before the fetch we snapshot scrollHeight. After loading
// flips back to false (older messages prepended), a useLayoutEffect adds
// (newHeight − oldHeight) to scrollTop so the message the reader was looking
// at stays put — no jump.
//
// @param {object} opts
// @param {React.RefObject<HTMLElement>} opts.scrollRef  ref on the overflow container
// @param {boolean} opts.hasMore        next_before present (more older pages exist)
// @param {boolean} opts.loading       a load-more is currently in flight
// @param {boolean} opts.ready         the stream finished its initial load (DOM mounted)
// @param {number} [opts.threshold]   px from top that triggers a fetch (default 120)
// @param {() => void} opts.onLoadMore  request the next (older) page
// @returns {{ sentinelRef: React.RefObject<HTMLDivElement> }}  place this <div>
//   as the FIRST child inside the scroll container (visual marker + test hook).
export function useInfiniteScrollTop({ scrollRef, hasMore, loading, ready, threshold = 120, onLoadMore }) {
  const sentinelRef = useRef(null);
  const prevHeightRef = useRef(0);
  const wasLoadingRef = useRef(false);
  const armedRef = useRef(false);

  // Synchronous loading mirror — set true the instant a loadMore starts (before
  // its await), not on the next render. The scroll listener reads this ref so a
  // scroll burst can't fire onLoadMore twice before the async state update
  // arrives. The two resulting identical-p_before requests were the dominant
  // source of duplicate messages in the DM stream.
  const loadingSyncRef = useRef(loading);
  useEffect(() => { loadingSyncRef.current = loading; }, [loading]);

  // Keep the latest callbacks/flags in refs so the scroll listener (attached
  // once) always reads fresh values without being torn down each render.
  const onLoadMoreRef = useRef(onLoadMore);
  const loadingRef = useRef(loading);
  const hasMoreRef = useRef(hasMore);
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
    loadingRef.current = loading;
    hasMoreRef.current = hasMore;
  });

  // Arm the listener after the initial scroll-to-bottom has had time to run.
  // Before arming, scrollTop is 0 and we must not fire a premature load.
  useEffect(() => {
    if (!ready) {
      armedRef.current = false;
      return;
    }
    const t = setTimeout(() => { armedRef.current = true; }, 300);
    return () => clearTimeout(t);
  }, [ready]);

  // Attach the scroll listener. Re-attached when `ready` or `hasMore` flip so
  // we add/remove the listener as needed (no listener when there are no more
  // pages).
  useEffect(() => {
    if (!ready) return;
    const el = scrollRef.current;
    if (!el || !hasMore) return;

    const onScroll = () => {
      if (!armedRef.current) return;
      if (!hasMoreRef.current || loadingRef.current || loadingSyncRef.current) return;
      if (el.scrollTop <= threshold) {
        prevHeightRef.current = el.scrollHeight;
        onLoadMoreRef.current?.();
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollRef, hasMore, ready]);

  // Anchor scroll position after a load-more completes (loading true → false).
  useLayoutEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    if (wasLoadingRef.current && !loading && prevHeightRef.current) {
      const added = root.scrollHeight - prevHeightRef.current;
      if (added > 0) root.scrollTop = root.scrollTop + added;
    }
    wasLoadingRef.current = loading;
  });

  return { sentinelRef };
}

export default useInfiniteScrollTop;

